import { Dispatch, SetStateAction, Suspense, useEffect, useState } from "react";
import { capitalize, capitalizeEveryWord, getPrepiBotBaseURL, useAxios } from "functions";
import { useAuth } from "../../../context/authContext";
import toast from "react-hot-toast";
import { ErrorBoundary } from "@sentry/nextjs";
import Button from "../../1_atom/buttons/button";
import { useRouter } from "next/router";
import { eSettingsTabName, isBoolean, isDefined } from "typings";
import { format } from "date-fns-tz";
import { SettingSection } from "./settingSection";
import { LoadingSpinner } from "../../1_atom/empty/notFound";
import { suspend } from "suspend-react";
import { BoostDistribution } from "../boosts/boostDistribution";
import BuyBoosts from "../pricing/buyBoosts";
import { getUserPremiumField } from "data";
import Link from "next/link";
import { CheckoutSuccessful } from "../../../global/state/notifications";

const PaymentsSettings = () => {
  const router = useRouter()
  const auth = useAuth()
  const [showBuyMoreBoosts, setShowBuyMoreBoosts] = useState<boolean>(false)
  const isRealUser = () => {
    if(!auth.user) return false
    if(("getIdToken" in auth.user) === false) return false
    return true
  }
  const authUserFound = isRealUser()

  useEffect(() => {
    if(router?.query?.["successful-checkout"] === "true"){
      const toastID = toast.loading('Loading...', { id: "successful-checkout"});

      toast.success((t) => <CheckoutSuccessful toastID={toastID} />, {
        id: toastID,
        duration: 12000,
      })
      router.push(
        {
          pathname: "/settings",
          query: { show: eSettingsTabName.PREMIUM }
        },
        undefined,
        { shallow: true }
      )
    }
  }, [])

  const UserContent = () => {
    const axios = useAxios({ getBearerToken: auth.getFirebaseAuthToken, baseURL: getPrepiBotBaseURL() })
    const router = useRouter()
    const subscriptions = auth?.userData?.[getUserPremiumField()]?.subscriptions || {}

    const manageSubscriptionCallback = async () => {
      toast.loading("Redirecting")
      const res = await axios.post(
        `/stripe-portal`,
        {},
        { validateStatus: () => true }
      )
      const url = res.data?.url
      if(!url) toast.error("Error: failed to get customer portal redirect URL.")
      router.push(url)
    }

    return (
      <ErrorBoundary fallback={<p>{eSettingsTabName.PREMIUM} content failed</p>}>
        <Suspense fallback={<LoadingSpinner />}>
          <NewSubscription showBuyMoreBoosts={showBuyMoreBoosts} setShowBuyMoreBoosts={setShowBuyMoreBoosts} />
          {Object.values(subscriptions)?.length > 0 && (
            <ActiveSubscriptions setShowBuyMoreBoosts={setShowBuyMoreBoosts} manageSubscriptionCallback={manageSubscriptionCallback} />
          )}
        </Suspense>
      </ErrorBoundary>
    )
  }
  
  return (
    <SettingSection title={capitalize(eSettingsTabName.PREMIUM)}>
      {authUserFound ? <UserContent /> : <LoadingSpinner />}
    </SettingSection>
  )
}

export default PaymentsSettings;

const ActiveSubscriptions = ({ setShowBuyMoreBoosts, manageSubscriptionCallback }: { setShowBuyMoreBoosts: Dispatch<SetStateAction<boolean>>, manageSubscriptionCallback: () => Promise<void>}) => {
  const auth = useAuth()
  const axios = useAxios({ getBearerToken: auth.getFirebaseAuthToken, baseURL: getPrepiBotBaseURL() })

  const MySubscriptions = () => {
    const subscriptions = suspend(
      async () => {
        const subs = (await axios.get(`/subscriptions`)).data as Array<unknown>
        if(isDefined(subs) && Array.isArray(subs) && subs?.length > 0){
          setShowBuyMoreBoosts(false)
          return subs
        }
        // No subscriptions found
        return []
      },
      [auth?.userData?.[getUserPremiumField()]?.subscriptions]
    )
    const subscriptionsValid = !!subscriptions && Array.isArray(subscriptions) && subscriptions?.length > 0
    console.log("🚀 ~ file: premiumSettings.tsx:86 ~ MySubscriptions ~ subscriptions", subscriptions)

    const ItemPrice = ({ item, discount }: { item: any, discount: any }) => {
      let amount = item?.price?.unit_amount * item?.quantity
      const currency = item?.price?.currency
      if(typeof amount !== "number") return <></>
      const dis = discount?.coupon?.percent_off
      const percentageOff = !!dis && typeof dis === "number" ? dis : 0
      if(percentageOff > 0) amount = amount * (percentageOff / 100)
      const showPrice = (amount / 100).toFixed(0)

      const getIntervalString = () => {
        const { interval_count, interval } = item?.price?.recurring
        if(!interval_count || interval_count === 1) return interval
        return `${interval_count} ${interval}s`
      }

      return (
        <div className="flex-col gap0">
          <span><span className="font-bold text-base">{showPrice} {currency.toUpperCase()}</span> <br />/{getIntervalString()}</span>
        </div>
      )
    }

    if (!subscriptionsValid) {
      return <p>You have no active subscriptions.</p>
    }

    const Subscription = ({ sub }: { sub: any }) => {
      const imgSrc = sub?.plan?.product?.images?.[0]

      const BillingDate = () => {
        const statusString =
          sub?.cancel_at_period_end === true
            ? "Cancelled"
            : capitalizeEveryWord(sub?.status?.replace("_", " ") || "")
        const nextBillingString =
          sub?.cancel_at_period_end === true ? "expires at" : "renews"
        return (
          <span className="weak-text">
            {statusString} - {nextBillingString}{" "}
            {format(new Date(sub.current_period_end * 1000), "dd. LLLL yyyy")}
          </span>
        )
      }

      const SubscriptionItem = ({ item }: { item: any }) => {
        const discount = sub?.discount?.coupon?.percent_off
        const discountExists = !!discount && discount !== 0

        return (
          <div key={sub.id} className="left-right-container align-center">
            <div className="left flex-row gap1">
              {!!imgSrc && (
                <img
                  src={imgSrc}
                  alt={sub?.plan?.product?.name || "subscription icon"}
                  style={{ height: 55, width: 55 }}
                />
              )}
              <div className="flex-col">
                <h5>{sub?.plan?.product?.name}</h5>
                {discountExists && <span>Applied discount: {discount}%</span>}
                <span>Quantity: {item?.quantity || 1}</span>
              </div>
            </div>
            <ItemPrice item={item} discount={sub?.discount} />
          </div>
        )
      }

      return (
        <div className="flex-col gap0 bg3 rounded p15">
          <BillingDate />
          {sub?.items?.data?.map((item: any) => <SubscriptionItem key={item?.id} item={item} />)}
        </div>
      )
    }

    return (
      <div className="flex-col w100 gap1">
        {/* @ts-ignore */}
        {subscriptions?.map((sub) => <Subscription key={sub?.id} sub={sub} />)}
      </div>
    )
  }

  return (
    <div className="flex-col gap1 align-start mt2">
      <h5>My subscriptions</h5>
      <MySubscriptions />
      <Button
        text="Manage my subscriptions"
        click={manageSubscriptionCallback}
        addClass="align-self-end"
      />
      <BoostDistribution />
    </div>
  )
}

const NewSubscription = ({ showBuyMoreBoosts, setShowBuyMoreBoosts }: { showBuyMoreBoosts: boolean, setShowBuyMoreBoosts: Dispatch<SetStateAction<boolean>> }) => {

  if(showBuyMoreBoosts === false) return (
    <Button fullWidth variant="soft" color="white-on-dark" text={showBuyMoreBoosts ? "Hide get more boosts" : "Get more boosts"} click={() => {
      setShowBuyMoreBoosts(prev => !prev)
    }} />
  )

  return (
    <>
      <BuyBoosts inSettings />
      <div className="flex-row center-center mt1">
        <Link href={"/pricing"} prefetch={false} target="_blank">
          <Button
            text="See full pricing table"
            color="white-on-dark"
            variant="transparent"
            size="small"
          />
        </Link>
      </div>
    </>
  )
}